import type { UserDataLoginRecord } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import { ApiErrorMessage, ErrorCodes, ErrorMessage, NoddiButton, useAppForm } from 'noddi-ui-web';
import { commonTexts, errorCodesEnglish, hasErrorCode, isPhoneNumberValid } from 'noddi-util';
import { useEffect, useRef, useState } from 'react';
import type { UserSessionData } from '../../contexts/AuthContext';
import { useAuthContext } from '../../contexts/useAuthContext';
import { SignupAccountMatch } from './SignupAccountMatch';
import { SignupLicensePlateFields } from './SignupLicensePlateFields';
import {
  confirmedSignupPreview,
  previewHasMatch,
  resolveVerifiedSignup,
  toVerifiedSignupInput,
  type ConfirmedSignupPreview,
  type VerifiedSignupInput
} from './verifiedSignup';
import { verifiedSignupFormSchema, type VerifiedSignupFormValues } from './verifiedSignupFormSchema';

type Props = {
  phoneNumber: string;
  signupChallengeToken: string;
  initialValues?: VerifiedSignupFormValues;
  onRestartVerification: (values: VerifiedSignupFormValues) => void;
  trackUserIdentity?: (userData: UserDataLoginRecord) => void;
  onLoginSuccess?: (userData: UserSessionData) => void;
  /** Ask for license plates to match an existing account. Off unless the brand needs it. */
  showLicensePlates?: boolean;
  translations?: {
    thisFieldIsRequired: string;
    thisFieldMustBeAtLeast2CharactersLong: string;
    mustProvideAValidEmailAddress: string;
    firstName: string;
    surname: string;
    email: string;
    password: string;
    userInformation: string;
    createUser: string;
    verificationExpired: string;
    verifyPhoneAgain: string;
    licensePlateNumber: string;
    addLicensePlate: string;
    removeLicensePlate: string;
    possibleExistingAccount: string;
    createNewAccount: string;
    emailAlreadyHasAccount: string;
    emailSignInLinkPrompt: string;
    sendSignInLink: string;
    signInLinkSent: string;
    signIn: string;
    forgotPassword: string;
    passwordResetSuccess: (email: string) => string;
    accessDeniedLoginMessage: string;
  };
  errorCodes?: ErrorCodes;
};

export const VerifiedSignupForm = ({
  phoneNumber,
  signupChallengeToken,
  initialValues,
  onRestartVerification,
  trackUserIdentity,
  onLoginSuccess,
  showLicensePlates = false,
  translations,
  errorCodes
}: Props) => {
  const texts = translations ?? commonTexts.registrationTranslations;
  const errorCodeTexts = errorCodes ?? errorCodesEnglish;
  const { loginUser } = useAuthContext();
  const [phoneError, setPhoneError] = useState<string | null>(null);
  // Submit reads the ref so a preview stored on the previous click is visible before the next render.
  const confirmedMatchRef = useRef<ConfirmedSignupPreview | null>(null);
  const [confirmedMatch, setConfirmedMatch] = useState<ConfirmedSignupPreview | null>(null);

  const { mutateAsync: previewSignup, error: previewError } = noddiAsync.usePost({
    type: URLKeys.postSignupReconciliationPreview,
    queryConfig: { meta: { suppressSuccessToast: true } }
  });
  const {
    mutateAsync: createVerifiedUser,
    isPending: isCreatingVerifiedUser,
    error: createVerifiedError
  } = noddiAsync.usePost({
    type: URLKeys.postSignupReconciliation
  });

  const {
    mutate: requestSignInLink,
    isPending: isRequestingSignInLink,
    isSuccess: signInLinkSent,
    error: signInLinkError,
    reset: resetSignInLink
  } = noddiAsync.usePost({
    type: URLKeys.postSignupReconciliationEmailLogin,
    queryConfig: { meta: { suppressSuccessToast: true } }
  });

  const signupErrors = [previewError, createVerifiedError, signInLinkError];
  const registrationError = signupErrors.find((error) => error !== null) ?? null;
  // Expired after ten minutes or reopened from another network: retrying the token can never succeed.
  const verificationExpired =
    hasErrorCode(signupErrors, 'signup_reconciliation_expired') ||
    hasErrorCode(signupErrors, 'signup_reconciliation_invalid');

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [ref]);

  const rememberConfirmedMatch = (preview: ConfirmedSignupPreview) => {
    confirmedMatchRef.current = preview;
    setConfirmedMatch(preview);
  };

  const createVerifiedAccount = async (input: VerifiedSignupInput) => {
    const { data } = await createVerifiedUser({
      challengeToken: signupChallengeToken,
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email
    });
    trackUserIdentity?.(data.user);
    loginUser(data);
    onLoginSuccess?.(data);
  };

  const createDespiteMatch = async (input: VerifiedSignupInput) => {
    try {
      await createVerifiedAccount(input);
    } catch {
      // The mutation error renders under the action.
    }
  };

  const form = useAppForm({
    defaultValues:
      initialValues ??
      ({
        firstName: '',
        surname: '',
        email: '',
        licensePlateNumbers: ['']
      } satisfies VerifiedSignupFormValues),
    validators: {
      onSubmit: verifiedSignupFormSchema(texts)
    },
    onSubmit: async ({ value }) => {
      if (verificationExpired) {
        return;
      }

      // The verified SMS number is not a form field. Refuse signup when it is missing.
      if (!isPhoneNumberValid(phoneNumber)) {
        setPhoneError(texts.thisFieldIsRequired);
        return;
      }
      setPhoneError(null);
      // A link sent for the previous submit must not read as sent for an edited email.
      resetSignInLink();

      const input = toVerifiedSignupInput(value);
      if (resolveVerifiedSignup(confirmedMatchRef.current, input).kind === 'warn-then-create') {
        await createVerifiedAccount(input);
        return;
      }

      const { data } = await previewSignup({
        challengeToken: signupChallengeToken,
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        licensePlateNumbers: input.licensePlateNumbers
      });

      if (previewHasMatch(data.matches)) {
        rememberConfirmedMatch(confirmedSignupPreview(input, data.matches, data.emailLoginAvailable));
        return;
      }

      await createVerifiedAccount(input);
    }
  });

  return (
    <form.AppForm>
      <form.Subscribe selector={(state) => state.values}>
        {(values) => {
          const next = toVerifiedSignupInput(values);
          const resolution = resolveVerifiedSignup(confirmedMatch, next);
          const showSubmit =
            !verificationExpired && (resolution.kind === 'preview' || resolution.kind === 'warn-then-create');
          return (
            <>
              <form
                className='flex flex-col gap-2'
                onSubmit={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void form.handleSubmit().catch(() => {
                    // The mutation error renders under the form.
                  });
                }}
              >
                <h6 className='font-semibold'>{texts.userInformation}</h6>
                <form.AppField name='firstName'>
                  {(field) => <field.Input ref={ref} label={texts.firstName} />}
                </form.AppField>
                <form.AppField name='surname'>{(field) => <field.Input label={texts.surname} />}</form.AppField>
                <form.AppField name='email'>{(field) => <field.Input label={texts.email} />}</form.AppField>
                {showLicensePlates && (
                  <SignupLicensePlateFields
                    Field={form.AppField}
                    label={texts.licensePlateNumber}
                    addLabel={texts.addLicensePlate}
                    removeLabel={texts.removeLicensePlate}
                  />
                )}
                {phoneError && <ErrorMessage message={phoneError} />}
                {resolution.kind === 'warn-then-create' && (
                  <div className='mt-2 rounded border p-3'>
                    <p>{texts.possibleExistingAccount}</p>
                  </div>
                )}
                {showSubmit && (
                  <form.SubscribeButton
                    className='mt-2 w-full'
                    label={resolution.kind === 'warn-then-create' ? texts.createNewAccount : texts.createUser}
                  />
                )}
              </form>
              {!verificationExpired && resolution.kind === 'email-login' && confirmedMatch && (
                <SignupAccountMatch
                  email={next.email}
                  emailLoginAvailable={confirmedMatch.emailLoginAvailable}
                  onSendSignInLink={() =>
                    requestSignInLink({ challengeToken: signupChallengeToken, email: next.email })
                  }
                  sendingSignInLink={isRequestingSignInLink}
                  signInLinkSent={signInLinkSent}
                  onCreateNewAccount={() => void createDespiteMatch(next)}
                  creatingNewAccount={isCreatingVerifiedUser}
                  onLoginSuccess={onLoginSuccess}
                  trackUserIdentity={trackUserIdentity}
                  translations={texts}
                />
              )}
              {verificationExpired ? (
                <div className='flex flex-col gap-4 pt-4'>
                  <ErrorMessage message={texts.verificationExpired} />
                  <NoddiButton type='button' className='w-full' onClick={() => onRestartVerification(values)}>
                    {texts.verifyPhoneAgain}
                  </NoddiButton>
                </div>
              ) : registrationError ? (
                <ApiErrorMessage errorCodes={errorCodeTexts} error={registrationError} />
              ) : null}
            </>
          );
        }}
      </form.Subscribe>
    </form.AppForm>
  );
};
